import {
  CenteredLayout,
  column,
  ContentHeader,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  Icon,
  numericColumn,
  Palette,
  simpleHeader,
  SimpleHeaderAndData,
  TagType,
  useComputed,
} from "@homebound/beam";
import { ObjectConfig } from "@homebound/form-state";
import { ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import { emptyCellDash, priceCell, tagCell } from "src/components";
import { Price } from "src/components/Price";
import { CommitmentStatus } from "src/generated/graphqlTypes";
import { useRefetchOnReturn } from "src/hooks/useRefetchOnReturn";
import { BaseChangeEventFormState } from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { InfoBanner } from "src/routes/projects/change-events/guided/components/InfoBanner";
import { StatTotals } from "src/routes/projects/change-events/guided/components/StatTotals";
import { fail, queryResult } from "src/utils";
import {
  ReconcileImpactedCommitments_CommitmentLikeFragment,
  ReconcileImpactedCommitmentsStep_CommitmentLikeFragment,
  useReconcileImpactedCommitmentsStepQuery,
} from "./ReconcileImpactedCommitmentsStep.generated";

type ReconcileImpactedCommitmentsStepProps<F extends ReconcileImpactedCommitmentsFormInput> = {
  formState: BaseChangeEventFormState<F>;
};

/**
 * The POs already written against the scope the change event touches (`ChangeEvent.impactedCommitments`), and what
 * has to happen to each of them by hand before the change event can be applied. Nothing here changes a PO: the step
 * is a checklist.
 */
export function ReconcileImpactedCommitmentsStep<F extends ReconcileImpactedCommitmentsFormInput>(
  props: ReconcileImpactedCommitmentsStepProps<F>,
) {
  const { formState } = props;
  // Only the workflows that save the change event before this step (apply partial config, add-on option) reach it.
  const changeEventId = useComputed(() => formState.id.value ?? fail("No change event id"), [formState]);
  const query = useReconcileImpactedCommitmentsStepQuery({ variables: { changeEventId } });
  // The user acts on the POs in other tabs, so pick up what they did when they come back.
  useRefetchOnReturn(query);
  return queryResult(query, ({ changeEvent }) => (
    <ReconcileImpactedCommitmentsView impactedCommitments={changeEvent.impactedCommitments} />
  ));
}

type Commitment = ReconcileImpactedCommitmentsStep_CommitmentLikeFragment;

type ReconcileImpactedCommitmentsViewProps = { impactedCommitments: Commitment[] };

function ReconcileImpactedCommitmentsView(props: ReconcileImpactedCommitmentsViewProps) {
  const { impactedCommitments } = props;
  const commitments = useMemo(() => reconcilableCommitments(impactedCommitments), [impactedCommitments]);
  const rows = useMemo(() => createRows(commitments), [commitments]);
  const columns = useMemo(() => createColumns(), []);
  return (
    <CenteredLayout size="sm">
      <div css={Css.df.fdc.gap3.$}>
        <ContentHeader
          title="Resolve Existing Work"
          description="We found the following commitments that are tied to selected scope in the new configuration. Review what needs to happen to each before continuing."
        />
        <StatTotals
          testId="impactedCommitmentsTotals"
          items={[
            {
              value: blue(<Price valueInCents={commitments.sum((c) => c.committedInCents)} />),
              label: "Committed Cost",
            },
            { value: blue(commitments.length), label: "Commitments to Resolve" },
            {
              value: blue(commitments.count((c) => reconcileAction(c).action === ReconcileAction.ContactPayables)),
              label: "Contact AP",
            },
          ]}
        />
        <InfoBanner
          variant="info"
          title="Commitments are not updated automatically"
          message="Complete the required actions outside this Change Event before submitting for approval."
        />
        <GridTable columns={columns} rows={rows} style={{ allWhite: true, rowHeight: "fixed" }} />
      </div>
    </CenteredLayout>
  );
}

type Row = SimpleHeaderAndData<Commitment>;

function createRows(commitments: Commitment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...commitments.map((c) => ({ kind: "data" as const, id: c.id, data: c }))];
}

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Reference",
      // Opened in a new tab, so the user can act on the PO without losing their place in the wizard
      data: (c) => ({
        content: (
          <Link target="_blank" to={c.blueprintUrl.path}>
            {c.shortName}
          </Link>
        ),
        value: c.shortName,
      }),
      w: "100px",
    }),
    column<Row>({
      header: "Status",
      data: (c) => {
        const { status, statusTagType } = reconcileAction(c);
        return tagCell(statusTagType, status);
      },
      w: "100px",
    }),
    column<Row>({ header: "Trade", data: (c) => c.tradePartner?.name ?? emptyCellDash, w: "250px" }),
    numericColumn<Row>({ header: "Amount", data: (c) => priceCell({ valueInCents: c.committedInCents }) }),
    column<Row>({
      header: "Action Required",
      data: (c) => {
        const { action, description } = reconcileAction(c);
        return {
          content: (
            <div css={Css.df.aic.gap1.$}>
              <span>{action}</span>
              <Icon icon="infoCircle" inc={2} color={Palette.Gray600} tooltip={description} />
            </div>
          ),
          value: action,
        };
      },
    }),
  ];
}

function blue(value: ReactNode): ReactNode {
  return <span css={Css.blue600.$}>{value}</span>;
}

function reconcilableCommitments<C extends Pick<ReconcileImpactedCommitments_CommitmentLikeFragment, "status">>(
  impactedCommitments: readonly C[],
): C[] {
  return impactedCommitments.filter((c) => c.status !== CommitmentStatus.Voided);
}

/**
 * Whether the change event touches any PO that has to be dealt with by hand before it can be applied. Derived from
 * the saved change event's `impactedCommitments` on every save (see {@link reconcileImpactedCommitmentsMapToForm}),
 * and kept on the form so the step can hide itself synchronously. Local-only, so it never dirties the form.
 */
export type ReconcileImpactedCommitmentsFormInput = {
  /** Whether the change touches any PO that isn't already voided, i.e. whether the Reconcile step has anything to show. */
  hasImpactedCommitments?: boolean | null;
};

export const reconcileImpactedCommitmentsFormConfig: ObjectConfig<ReconcileImpactedCommitmentsFormInput> = {
  hasImpactedCommitments: { type: "value", isLocalOnly: true },
};

export function reconcileImpactedCommitmentsMapToForm(changeEvent: {
  impactedCommitments: readonly ReconcileImpactedCommitments_CommitmentLikeFragment[];
}): ReconcileImpactedCommitmentsFormInput {
  return { hasImpactedCommitments: reconcilableCommitments(changeEvent.impactedCommitments).nonEmpty };
}

enum ReconcileAction {
  Delete = "Delete",
  Void = "Void",
  ContactPayables = "Contact Payables",
}

type ReconcileResolution = {
  action: ReconcileAction;
  description: string;
  /**
   * The PO's state as the step words it, which is what decides the action: Draft, Issued (sent / signed, nothing
   * billed) or Applied (a bill against it). Deliberately not the PO's own status text.
   */
  status: string;
  statusTagType: TagType;
};

/**
 * What has to happen to a PO before the change can be applied, per its state:
 *
 * - a draft (not yet issued to the trade) is deleted, and procurement issues a new PO from the new configuration;
 * - an issued PO with nothing billed against it is voided in full, which releases the committed budget;
 * - a PO with a bill applied against it can't be voided, so Accounts Payable has to reverse the bill (or a change
 *   order be issued) before the change event can go ahead.
 *
 * "Billed" goes by `pendingBilledInCents` rather than `billedInCents`, mirroring `Commitment.canBeVoided`: a PO whose
 * bill is still pending approval can't be voided either, so telling the user to void it would only fail later.
 */
function reconcileAction(commitment: ReconcileImpactedCommitments_CommitmentLikeFragment): ReconcileResolution {
  if (commitment.status === CommitmentStatus.Draft) {
    return {
      action: ReconcileAction.Delete,
      description:
        "The draft PO will be deleted. Procurement will issue a new PO from the new configuration and its bid contracts.",
      status: "Draft",
      statusTagType: "neutral",
    };
  } else if ((commitment.pendingBilledInCents ?? 0) > 0) {
    return {
      action: ReconcileAction.ContactPayables,
      description:
        "A PO cannot be voided once a bill exists against it. Contact Accounts Payable to reverse the bill if possible, or issue a change order to the trade partner.",
      status: "Applied",
      statusTagType: "warning",
    };
  }
  return {
    action: ReconcileAction.Void,
    description: "The PO will be voided in full and the committed budget will be released.",
    status: "Issued",
    statusTagType: "neutral",
  };
}
